import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

const TOKEN_UPDATED_EVENT = "token_updated"; // fires for either system; listeners re-read whichever key they care about

const notifyTokenUpdated = () => {
  window.dispatchEvent(new Event(TOKEN_UPDATED_EVENT));
};

// Separate refresh locks per system -- a B2B refresh in flight shouldn't
// block/merge with a B2C refresh in flight (distinct users, distinct tokens).
let isRefreshingB2C = false;
let pendingB2CRequests: Array<(token: string | null) => void> = [];

let isRefreshingB2B = false;
let pendingB2BRequests: Array<(token: string | null) => void> = [];

const subscribe = (queue: Array<(token: string | null) => void>, cb: (token: string | null) => void) => {
  queue.push(cb);
};

const flush = (queue: Array<(token: string | null) => void>, token: string | null) => {
  queue.forEach((cb) => cb(token));
  queue.length = 0;
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url: string = originalRequest?.url ?? "";

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    const isB2CRequest = url.includes("/b2c/");
    const isB2BRequest = !isB2CRequest && (url.includes("/api/") || true); // adjust if you have a cleaner B2B url signature

    // ---- B2C branch ----
    if (isB2CRequest) {
      const isAuthEndpoint =
        url.includes("/b2c/refresh-token") ||
        url.includes("/b2c/login") ||
        url.includes("/b2c/logout");

      if (isAuthEndpoint) return Promise.reject(error);

      originalRequest._retry = true;
      const refreshToken = localStorage.getItem("b2c_refresh_token");

      if (!refreshToken) {
        localStorage.removeItem("b2c_token");
        localStorage.removeItem("b2c_refresh_token");
        notifyTokenUpdated();
        window.location.href = "/practice/login";
        return Promise.reject(error);
      }

      if (isRefreshingB2C) {
        return new Promise((resolve, reject) => {
          subscribe(pendingB2CRequests, (newToken) => {
            if (!newToken) return reject(error);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(axios(originalRequest));
          });
        });
      }

      isRefreshingB2C = true;
      try {
        const { data } = await axios.post(`${apiUrl}/b2c/refresh-token`, {
          refresh_token: refreshToken,
        });
        localStorage.setItem("b2c_token", data.access_token);
        notifyTokenUpdated();
        isRefreshingB2C = false;
        flush(pendingB2CRequests, data.access_token);
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return axios(originalRequest);
      } catch (refreshError) {
        isRefreshingB2C = false;
        flush(pendingB2CRequests, null);
        localStorage.removeItem("b2c_token");
        localStorage.removeItem("b2c_refresh_token");
        notifyTokenUpdated();
        window.location.href = "/practice/login";
        return Promise.reject(refreshError);
      }
    }

    // ---- B2B branch ----
    const isAuthEndpoint =
      url.includes("/refresh-token") ||
      url.includes("/login") ||
      url.includes("/logout");

    if (isAuthEndpoint) return Promise.reject(error);

    originalRequest._retry = true;
    const refreshToken = localStorage.getItem("refresh_token");

    if (!refreshToken) {
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      notifyTokenUpdated();
      const tenantSlug = localStorage.getItem("tenantSlug");
      window.location.href = tenantSlug ? `/platform/${tenantSlug}/login` : "/platform/";
      return Promise.reject(error);
    }

    if (isRefreshingB2B) {
      return new Promise((resolve, reject) => {
        subscribe(pendingB2BRequests, (newToken) => {
          if (!newToken) return reject(error);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          resolve(axios(originalRequest));
        });
      });
    }

    isRefreshingB2B = true;
    try {
      const { data } = await axios.post(`${apiUrl}/refresh-token`, {
        refresh_token: refreshToken,
      });
      localStorage.setItem("token", data.access_token);
      notifyTokenUpdated();
      isRefreshingB2B = false;
      flush(pendingB2BRequests, data.access_token);
      originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
      return axios(originalRequest);
    } catch (refreshError) {
      isRefreshingB2B = false;
      flush(pendingB2BRequests, null);
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      notifyTokenUpdated();
      const tenantSlug = localStorage.getItem("tenantSlug");
      window.location.href = tenantSlug ? `/platform/${tenantSlug}/login` : "/platform/";
      return Promise.reject(refreshError);
    }
  }
);

export { TOKEN_UPDATED_EVENT };
export default axios;